/**
 * Stream B — B24: Email Deliverability Dashboard
 *
 * Sender reputation, bounce rate, spam complaints, blacklist status,
 * warm-up progress, authentication status (SPF/DKIM/DMARC).
 * Real API, no mocks.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { formatDate } from "@/lib/i18n";
import { Shield, AlertTriangle, CheckCircle2, XCircle, TrendingUp, Mail } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

interface DeliverabilityData {
  reputationScore: number;
  bounceRate: number;
  spamComplaintRate: number;
  warmupProgress: number;
  authentication: {
    spf: "pass" | "fail" | "missing";
    dkim: "pass" | "fail" | "missing";
    dmarc: "pass" | "fail" | "missing";
  };
  blacklists: Array<{ name: string; listed: boolean; checkedAt: string }>;
  trends: {
    bounceRate7d: number[];
    spamRate7d: number[];
  };
}

interface DnsRecord {
  type: "CNAME" | "TXT" | "MX";
  name: string;
  value: string;
  purpose: "dkim" | "spf" | "dmarc" | "bounce";
}

interface EmailDomainSetupData {
  configured: boolean;
  status: string;
  sendingDomain: string | null;
  sesRegion: string | null;
  configurationSetName: string | null;
  authentication: DeliverabilityData["authentication"];
  dnsRecords: DnsRecord[];
  verifiedAt: {
    dkim: string | null;
    spf: string | null;
    dmarc: string | null;
  };
  updatedAt: string | null;
}

const AUTH_STATUS_STYLES = {
  pass: { icon: CheckCircle2, color: "text-[var(--color-success)]", bg: "bg-[var(--color-success)]/10", label: "Pass" },
  fail: { icon: XCircle, color: "text-[var(--color-error)]", bg: "bg-[var(--color-error)]/10", label: "Fail" },
  missing: { icon: AlertTriangle, color: "text-[var(--color-warning)]", bg: "bg-[var(--color-warning)]/10", label: "Not Set" },
} as const;

function describeSetupStatus(setup: EmailDomainSetupData | null): string {
  if (!setup?.configured) return "Not configured";
  if (setup.status === "active") return "Active";
  if (setup.status === "verifying_dkim") return "Waiting for DNS verification";
  if (setup.status === "suspended") return "Suspended";
  return setup.status;
}

function DomainSetupPanel({
  setup,
  setupDomain,
  setupError,
  isProvisioning,
  isVerifying,
  onDomainChange,
  onProvision,
  onVerify,
}: {
  setup: EmailDomainSetupData | null;
  setupDomain: string;
  setupError: string | null;
  isProvisioning: boolean;
  isVerifying: boolean;
  onDomainChange: (domain: string) => void;
  onProvision: () => void;
  onVerify: () => void;
}) {
  const canSubmit = setupDomain.trim().length > 0 && !isProvisioning;

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted-foreground)]">Sending domain</p>
          <h2 className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
            {setup?.sendingDomain ?? "Configure authenticated email"}
          </h2>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            {setup?.configured
              ? `${describeSetupStatus(setup)}. Add the DNS records below, then verify.`
              : "Provision an SES identity and get the exact SPF, DKIM, DMARC, and bounce records to add in DNS."}
          </p>
        </div>
        <span className="rounded-full bg-[var(--color-surface)] px-3 py-1 text-xs font-medium text-[var(--color-foreground)]">
          {describeSetupStatus(setup)}
        </span>
      </div>

      <div className="mt-4 flex flex-col gap-2 md:flex-row">
        <input
          value={setupDomain}
          onChange={(event) => onDomainChange(event.target.value)}
          placeholder="mail.example.com"
          className="min-h-10 flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 text-sm text-[var(--color-foreground)]"
        />
        <button
          type="button"
          onClick={onProvision}
          disabled={!canSubmit}
          className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isProvisioning ? "Provisioning..." : setup?.configured ? "Re-provision" : "Provision domain"}
        </button>
        <button
          type="button"
          onClick={onVerify}
          disabled={!setup?.configured || isVerifying}
          className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isVerifying ? "Checking..." : "Verify DNS"}
        </button>
      </div>

      {setupError ? (
        <p className="mt-3 rounded-lg bg-[var(--color-error)]/10 p-3 text-sm text-[var(--color-error)]">{setupError}</p>
      ) : null}

      {setup?.dnsRecords.length ? (
        <div className="mt-4 overflow-hidden rounded-lg border border-[var(--color-border)]">
          <table className="w-full text-left text-sm">
            <thead className="bg-[var(--color-surface)] text-xs uppercase text-[var(--color-muted-foreground)]">
              <tr>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Value</th>
                <th className="px-3 py-2">Purpose</th>
              </tr>
            </thead>
            <tbody>
              {setup.dnsRecords.map((record) => (
                <tr key={`${record.type}:${record.name}:${record.purpose}`} className="border-t border-[var(--color-border)]">
                  <td className="px-3 py-2 font-mono text-xs">{record.type}</td>
                  <td className="px-3 py-2 font-mono text-xs">{record.name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{record.value}</td>
                  <td className="px-3 py-2 capitalize">{record.purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function ScoreGauge({ score }: { score: number }) {
  const color = score >= 80 ? "var(--color-success)" : score >= 50 ? "var(--color-warning)" : "var(--color-error)";
  const circumference = 2 * Math.PI * 45;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width="120" height="120" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r="45" fill="none" stroke="var(--color-border)" strokeWidth="8" />
        <circle
          cx="60" cy="60" r="45" fill="none"
          stroke={color} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          transform="rotate(-90 60 60)"
          className="transition-all duration-700"
        />
      </svg>
      <div className="absolute text-center">
        <span className="text-2xl font-bold" style={{ color }}>{score}</span>
        <span className="block text-xs text-[var(--color-muted-foreground)]">/ 100</span>
      </div>
    </div>
  );
}

function MiniTrend({ data, label }: { data: number[]; label: string }) {
  if (data.length === 0) return null;
  const max = Math.max(...data, 1);
  const width = 120;
  const height = 32;
  const points = data.map((v, i) => `${(i / Math.max(data.length - 1, 1)) * width},${height - (v / max) * height}`).join(" ");

  return (
    <div>
      <p className="text-xs text-[var(--color-muted-foreground)] mb-1">{label} (7d)</p>
      <svg width={width} height={height} className="overflow-visible">
        <polyline points={points} fill="none" stroke="var(--color-primary)" strokeWidth="1.5" />
      </svg>
    </div>
  );
}

export default function DeliverabilityDashboard() {
  const [data, setData] = useState<DeliverabilityData | null>(null);
  const [setup, setSetup] = useState<EmailDomainSetupData | null>(null);
  const [setupDomain, setSetupDomain] = useState("");
  const [setupError, setSetupError] = useState<string | null>(null);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [deliverabilityRes, setupRes] = await Promise.all([
        apiFetch<{ data: DeliverabilityData | null }>("/email-marketing/deliverability"),
        apiFetch<{ data: EmailDomainSetupData }>("/email-marketing/deliverability/setup"),
      ]);
      setData(deliverabilityRes.data);
      setSetup(setupRes.data);
      setSetupDomain(setupRes.data.sendingDomain ?? "");
    } catch {
      setData(null);
      setSetup(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const provisionDomain = useCallback(async () => {
    if (!setupDomain.trim()) return;
    setIsProvisioning(true);
    setSetupError(null);
    try {
      const res = await apiFetch<{ data: EmailDomainSetupData }>("/email-marketing/deliverability/setup", {
        method: "POST",
        body: JSON.stringify({ sendingDomain: setupDomain.trim() }),
      });
      setSetup(res.data);
      setSetupDomain(res.data.sendingDomain ?? setupDomain.trim());
    } catch (err) {
      setSetupError(err instanceof Error ? err.message : "Could not provision sending domain");
    } finally {
      setIsProvisioning(false);
    }
  }, [setupDomain]);

  const verifyDomain = useCallback(async () => {
    setIsVerifying(true);
    setSetupError(null);
    try {
      const res = await apiFetch<{ data: EmailDomainSetupData }>("/email-marketing/deliverability/setup/verify", {
        method: "POST",
      });
      setSetup(res.data);
    } catch (err) {
      setSetupError(err instanceof Error ? err.message : "Could not verify sending domain");
    } finally {
      setIsVerifying(false);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64 rounded" />
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map((i) => <SkeletonCard key={i} className="h-40 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Email Deliverability</h1>
        <DomainSetupPanel
          setup={setup}
          setupDomain={setupDomain}
          setupError={setupError}
          isProvisioning={isProvisioning}
          isVerifying={isVerifying}
          onDomainChange={setSetupDomain}
          onProvision={provisionDomain}
          onVerify={verifyDomain}
        />
        <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center">
          <Mail className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)] mb-3" />
          <p className="font-medium text-[var(--color-foreground)]">No deliverability data yet</p>
          <p className="text-sm text-[var(--color-muted-foreground)] mt-1">Send your first email campaign to start tracking deliverability metrics.</p>
          <a href="/email/campaigns" className="mt-3 inline-block text-sm text-[var(--color-primary)] hover:underline">
            Go to campaigns
          </a>
        </div>
      </div>
    );
  }

  const repColor = data.reputationScore >= 80 ? "var(--color-success)" : data.reputationScore >= 50 ? "var(--color-warning)" : "var(--color-error)";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Shield className="h-5 w-5 text-[var(--color-primary)]" />
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Email Deliverability</h1>
      </div>

      <DomainSetupPanel
        setup={setup}
        setupDomain={setupDomain}
        setupError={setupError}
        isProvisioning={isProvisioning}
        isVerifying={isVerifying}
        onDomainChange={setSetupDomain}
        onProvision={provisionDomain}
        onVerify={verifyDomain}
      />

      {/* Top metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        {/* Reputation score */}
        <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 flex flex-col items-center">
          <p className="text-xs text-[var(--color-muted-foreground)] mb-2">Sender Reputation</p>
          <ScoreGauge score={data.reputationScore} />
          <p className="mt-2 text-xs font-medium" style={{ color: repColor }}>
            {data.reputationScore >= 80 ? "Good" : data.reputationScore >= 50 ? "Fair" : "Poor"}
          </p>
        </div>

        {/* Bounce rate */}
        <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <p className="text-xs text-[var(--color-muted-foreground)]">Bounce Rate</p>
          <p className={`mt-1 text-3xl font-bold ${data.bounceRate > 5 ? "text-[var(--color-error)]" : data.bounceRate > 2 ? "text-[var(--color-warning)]" : "text-[var(--color-success)]"}`}>
            {data.bounceRate.toFixed(1)}%
          </p>
          <p className="text-xs text-[var(--color-muted-foreground)] mt-1">
            {data.bounceRate > 5 ? "High — clean your list" : data.bounceRate > 2 ? "Moderate — monitor closely" : "Healthy"}
          </p>
          <MiniTrend data={data.trends.bounceRate7d} label="Bounce trend" />
        </div>

        {/* Spam complaint rate */}
        <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <p className="text-xs text-[var(--color-muted-foreground)]">Spam Complaint Rate</p>
          <p className={`mt-1 text-3xl font-bold ${data.spamComplaintRate > 0.1 ? "text-[var(--color-error)]" : "text-[var(--color-success)]"}`}>
            {data.spamComplaintRate.toFixed(3)}%
          </p>
          <p className="text-xs text-[var(--color-muted-foreground)] mt-1">
            {data.spamComplaintRate > 0.1 ? "Above 0.1% — risk of throttling" : "Within safe threshold"}
          </p>
          <MiniTrend data={data.trends.spamRate7d} label="Spam trend" />
        </div>

        {/* Warm-up progress */}
        <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <p className="text-xs text-[var(--color-muted-foreground)]">Domain Warm-Up</p>
          <p className="mt-1 text-3xl font-bold text-[var(--color-foreground)]">{data.warmupProgress}%</p>
          <div className="mt-2 h-2 rounded-full bg-[var(--color-surface)] overflow-hidden">
            <div
              className="h-full rounded-full bg-[var(--color-primary)] transition-all"
              style={{ width: `${data.warmupProgress}%` }}
            />
          </div>
          <p className="text-xs text-[var(--color-muted-foreground)] mt-1">
            {data.warmupProgress >= 100 ? "Fully warmed up" : `${100 - data.warmupProgress}% remaining`}
          </p>
        </div>
      </div>

      {/* Authentication status */}
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
        <h3 className="mb-3 font-medium text-[var(--color-foreground)]">Email Authentication</h3>
        <div className="grid gap-3 md:grid-cols-3">
          {(["spf", "dkim", "dmarc"] as const).map((key) => {
            const status = data.authentication[key];
            const style = AUTH_STATUS_STYLES[status];
            const Icon = style.icon;
            return (
              <div key={key} className={`stagger-item flex items-center gap-3 rounded-lg ${style.bg} p-3`}>
                <Icon className={`h-5 w-5 ${style.color}`} />
                <div>
                  <p className="text-sm font-medium text-[var(--color-foreground)] uppercase">{key}</p>
                  <p className={`text-xs ${style.color}`}>{style.label}</p>
                </div>
              </div>
            );
          })}
        </div>
        {(data.authentication.spf === "missing" || data.authentication.dkim === "missing" || data.authentication.dmarc === "missing") && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-[var(--color-warning)]/5 p-3">
            <AlertTriangle className="h-4 w-4 text-[var(--color-warning)] mt-0.5" />
            <p className="text-sm text-[var(--color-warning)]">
              Missing authentication records reduce deliverability. Configure SPF, DKIM, and DMARC in your DNS settings.
            </p>
          </div>
        )}
      </div>

      {/* Blacklist status */}
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
        <h3 className="mb-3 font-medium text-[var(--color-foreground)]">Blacklist Monitoring</h3>
        {data.blacklists.length === 0 ? (
          <p className="text-sm text-[var(--color-muted-foreground)]">No blacklist data available. Checks run daily.</p>
        ) : (
          <div className="space-y-2">
            {data.blacklists.map((bl) => (
              <div key={bl.name} className="stagger-item flex items-center justify-between rounded-lg bg-[var(--color-surface)] p-3">
                <div className="flex items-center gap-2">
                  {bl.listed ? (
                    <XCircle className="h-4 w-4 text-[var(--color-error)]" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 text-[var(--color-success)]" />
                  )}
                  <span className="text-sm text-[var(--color-foreground)]">{bl.name}</span>
                </div>
                <div className="text-right">
                  <span className={`text-xs font-medium ${bl.listed ? "text-[var(--color-error)]" : "text-[var(--color-success)]"}`}>
                    {bl.listed ? "Listed" : "Clean"}
                  </span>
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    {formatDate(bl.checkedAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recommendations */}
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="h-4 w-4 text-[var(--color-primary)]" />
          <h3 className="font-medium text-[var(--color-foreground)]">Recommendations</h3>
        </div>
        <div className="space-y-2 text-sm text-[var(--color-muted-foreground)]">
          {data.bounceRate > 5 && (
            <p>Clean your email list — remove hard bounces and invalid addresses to reduce bounce rate below 2%.</p>
          )}
          {data.spamComplaintRate > 0.1 && (
            <p>Review your unsubscribe flow — make it easy to opt out. High spam complaints damage sender reputation.</p>
          )}
          {data.authentication.dmarc === "missing" && (
            <p>Set up DMARC policy to protect your domain from spoofing and improve inbox placement.</p>
          )}
          {data.warmupProgress < 100 && (
            <p>Continue gradual volume increase during warm-up. Sending too fast can trigger spam filters.</p>
          )}
          {data.reputationScore >= 80 && data.bounceRate <= 2 && data.spamComplaintRate <= 0.05 && (
            <p className="text-[var(--color-success)]">Your deliverability health is excellent. Keep monitoring.</p>
          )}
        </div>
      </div>
    </div>
  );
}
